import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FacebookShareButton } from 'react-share';
import ReactTooltip from 'react-tooltip';

import PhotoGridViewerActionButton from '../../../../../../../components/grid/components/PhotoGridViewerActionButton';
import useBasename from '../../../../../../../components/routing/useBasename';
import FeatureBlock from '../../../../../../../components/contextual/FeatureBlock';
import {
  canAccessForegroundUpdateOnBasename,
  canAccessOriginalDownloadsOnBasename,
  canAccessSpotlightOnBasename,
} from '../../../../../../../util/product-permissions';
import DownloadIcon from '../../../../../../../components/icons/DownloadIcon';
import PhotosIcon from '../../../../../../../components/icons/PhotosIcon';
import TrashIcon from '../../../../../../../components/icons/TrashIcon';
import TagIcon from '../../../../../../../components/icons/TagIcon';
import SpotlightIcon from '../../../../../../../components/icons/SpotlightIcon';
import FaceMatchingIcon from '../../../../../../../components/icons/FaceMatchingIcon';
import FacebookSquareIcon from '../../../../../../../components/icons/FacebookSquareIcon';
import { NoContributorPermissionBlock } from '../../../../../services/contributors';
import useContributorPermission from '../../../../../services/contributors/hooks/useContributorPermission';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import {
  PHOTO_ALBUM_FOLDER_TAGS,
  SHARE_BLOCK_MODAL,
} from '../../../../../../../constants/modals';
import usePhotoDownload from '../../../../../../../hooks/usePhotoDownload';
import getConfig from '../../../../../../../util/config';
import { removeHashes } from '../../../../../../../util/text';
import CoverPhotoButton from '../../../AlbumPhotoViewer/CoverPhotoButton';
import PhotoForFacesHighlightModal from '../../../../../components/FaceHighlightsPhotoGrid/PhotoForFacesHighlight/modal';
import FaceTagPhotoModal from '../../../../FaceTagPhoto/modal';
import useRemoveNjrBibMatch from '../../../../../../../hooks/useRemoveNjrBibMatch';
import { ALBUM_PHOTO_STATUS } from '../../../../../../../constants/albums';
import EyeSlashIcon from '../../../../../../../components/icons/EyeSlashIcon';
import { isMobile } from '../../../../../../../util/device-detector';
import useRequestConfirm from '../../../../../../../hooks/useRequestConfirm';
import FavoritesIcon from '../../../../../../../components/icons/FavoritesIcon';
import SlideshowIcon from '../../../../../../../components/icons/SlideshowIcon';
import CheckmarkIcon from '../../../../../../../components/icons/CheckmarkIcon';
import FavoritesEmptyIcon from '../../../../../../../components/icons/FavoritesEmptyIcon';
import EyeIcon from '../../../../../../../components/icons/EyeIcon';
import PlicBooksSuccessIcon from '../../../../../../../components/icons/PlicBooksSuccessIcon';
import PlicBooksUploadIcon from '../../../../../../../components/icons/PlicBooksUploadIcon';
import { getAsset } from '../../../../../../../util/assets';
import Button from '../../../../../../../components/global/Button';
import DropdownMenu from '../../../../../../../components/global/DropdownMenu';
import PhotoCropModal from '../../../../../../../components/global/PhotoCropModal';
import CropIcon from '../../../../../../../components/icons/CropIcon';
import InfoCircleIcon from '../../../../../../../components/icons/InfoCircleIcon';
import { YEARBOOK_ROSTER_IDENTITY_MODAL } from '../../../../../../../components/global/YearbookRosterIdentityModal';
import InfoCircleTip from '../../../../../../../components/global/InfoCircleTip';
import ZoomInIcon from '../../../../../../../components/icons/ZoomInIcon';
import ZoomOutIcon from '../../../../../../../components/icons/ZoomOutIcon';
import ShirtIcon from '../../../../../../../components/icons/ShirtIcon';

const propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_album_slideshow_photo: PropTypes.bool,
    album_oauth_link_photo_exists: PropTypes.bool,
  }).isRequired,
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onDelete: PropTypes.func,
  onVisibilityToggle: PropTypes.func,
  onSlideshowVisibilityToggle: PropTypes.func,
  onFavoritePhotoToggle: PropTypes.func,
  isFavorite: PropTypes.bool,
  ignorePhotoStatus: PropTypes.bool,
  hasSlideshow: PropTypes.bool,
  onSlideshowToggle: PropTypes.func,
  onPlicBookPhotosExport: PropTypes.func,
  onForegroundImageToggle: PropTypes.func,
  isForegroundImage: PropTypes.bool,
  hasYearbookPhoto: PropTypes.bool,
  matchedPhotoId: PropTypes.string,
  defaultCrop: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  onPrevRecord: PropTypes.func,
  onNextRecord: PropTypes.func,
  prevRecordId: PropTypes.string,
  nextRecordId: PropTypes.string,
  onFaceTagTotallyVoteCast: PropTypes.func,
  onFaceTagNopeVoteCast: PropTypes.bool,
  shareActionEnabled: PropTypes.bool,
  coverPhotoActionEnabled: PropTypes.bool,
  zoomIn: PropTypes.func,
  zoomOut: PropTypes.func,
};

const PhotoGroupPhotoViewerActions = ({
  album,
  photo,
  identity,
  onDelete,
  onVisibilityToggle,
  onSlideshowVisibilityToggle,
  onFavoritePhotoToggle,
  isFavorite,
  ignorePhotoStatus,
  hasSlideshow,
  onSlideshowToggle,
  onPlicBookPhotosExport,
  onForegroundImageToggle,
  isForegroundImage,
  hasYearbookPhoto,
  matchedPhotoId,
  defaultCrop,
  onPrevRecord,
  onNextRecord,
  prevRecordId,
  nextRecordId,
  onFaceTagTotallyVoteCast,
  onFaceTagNopeVoteCast,
  shareActionEnabled = true,
  coverPhotoActionEnabled = true,
  zoomIn,
  zoomOut,
}) => {
  const basename = useBasename();
  const { isContributor } = useContributorPermission({
    entityId: album?.id,
  });
  const openModal = useOpenModal();
  const requestConfirm = useRequestConfirm();
  const { handleDownload, loading: loadingDownload } = usePhotoDownload({
    photoId: photo.id,
  });
  const [highlightsMode, setHighlightsMode] = useState(false);
  const [faceTagMode, setFaceTagMode] = useState(false);
  const [cropMode, setCropMode] = useState(false);
  const isReferencePhotosAlbum = !!album?.is_reference_photos_album;
  const {
    matches: njrBibMatches,
    hasMatches: hasNjrBibMatches,
    removeMatches: removeNjrBibMatches,
    removing: removingNjrBibMatches,
  } = useRemoveNjrBibMatch({
    photoId: photo.id,
    albumId: album?.id,
    skip: isReferencePhotosAlbum,
  });

  const onRemoveNjrBibMatches = () => {
    const names = njrBibMatches
      .map(match =>
        [match.identity?.name, match.identity?.last_name]
          .filter(Boolean)
          .join(' '),
      )
      .filter(Boolean);

    requestConfirm({
      message: (
        <h3 className="text-center">
          Remove the jersey / bib {names.length > 1 ? 'matches' : 'match'} for{' '}
          <span className="text-primary">{names.join(', ')}</span> from this
          photo?
        </h3>
      ),
      confirmText: 'Remove',
      cancelText: 'Cancel',
      confirmBtnStyle: 'danger',
      onConfirm: () => removeNjrBibMatches(),
      onCancel: () => {},
    });
  };
  const isPublished =
    photo?.album_photo_status === ALBUM_PHOTO_STATUS.PUBLISHED ||
    ignorePhotoStatus;

  return (
    <Fragment>
      <NoContributorPermissionBlock entityId={album.id}>
        {onPlicBookPhotosExport && (
          <PhotoGridViewerActionButton
            label="Export photos to PLIC Books"
            className="photo-grid-viewer-action-button-lg"
            icon={
              photo?.album_oauth_link_photo_exists ? (
                <PlicBooksSuccessIcon color="#fff" size={54} />
              ) : (
                <PlicBooksUploadIcon color="#fff" size={54} />
              )
            }
            onClick={onPlicBookPhotosExport}
          />
        )}
        {(zoomIn || zoomOut) && (
          <Fragment>
            <PhotoGridViewerActionButton
              label="Zoom In"
              icon={<ZoomInIcon />}
              onClick={zoomIn}
            />
            <PhotoGridViewerActionButton
              label="Zoom Out"
              icon={<ZoomOutIcon />}
              onClick={zoomOut}
            />
          </Fragment>
        )}
        {!isReferencePhotosAlbum && (
          <Fragment>
            {photo?.foreground?.original_url &&
              canAccessForegroundUpdateOnBasename(basename) && (
                <InfoCircleTip
                  id={`${photo?.id}-foreground-update-info`}
                  className="mx-1"
                  tipProps={{
                    'data-place': 'bottom',
                  }}
                  tip={
                    <span>
                      You can update the knocked-out version of
                      <br />
                      this photo via the 3-dots menu accessible
                      <br />
                      through the photo&apos;s thumbnail. Additionally
                      <br />
                      you can scroll through the backdrops enabled
                      <br />
                      on your event through the small arrows when
                      <br />
                      foreground mode is enabled.
                    </span>
                  }
                />
              )}
            {onForegroundImageToggle && (
              <PhotoGridViewerActionButton
                label={
                  isForegroundImage
                    ? 'View Original Image'
                    : 'View Foreground only'
                }
                icon={
                  <img
                    src={getAsset(`/icon-background-removed.svg`)}
                    alt={
                      isForegroundImage
                        ? 'View Original Image'
                        : 'View Foreground Only'
                    }
                  />
                }
                onClick={() => onForegroundImageToggle()}
              />
            )}
            {isPublished && (
              <PhotoGridViewerActionButton
                label="Face Tag"
                icon={<FaceMatchingIcon />}
                onClick={() => setFaceTagMode(true)}
              />
            )}
            {hasYearbookPhoto && (
              <PhotoGridViewerActionButton
                label="Adjust crop"
                icon={<CropIcon />}
                onClick={() => setCropMode(true)}
              />
            )}
            {hasYearbookPhoto && identity && (
              <PhotoGridViewerActionButton
                label="Student Data"
                icon={<InfoCircleIcon />}
                onClick={() =>
                  openModal(YEARBOOK_ROSTER_IDENTITY_MODAL, {
                    albumId: album?.id,
                    rosterIdentityId: identity?.roster_identity?.id,
                    identity,
                  })
                }
              />
            )}
            {hasNjrBibMatches && (
              <PhotoGridViewerActionButton
                label="Remove jersey / bib matches"
                icon={<ShirtIcon />}
                disabled={removingNjrBibMatches}
                onClick={onRemoveNjrBibMatches}
              />
            )}
            <FaceTagPhotoModal
              forceOpen={faceTagMode}
              photoId={photo.id}
              albumId={album.id}
              onClose={() => setFaceTagMode(false)}
              onPrevRecord={onPrevRecord}
              onNextRecord={onNextRecord}
              prevRecordId={prevRecordId}
              nextRecordId={nextRecordId}
              onFaceTagTotallyVoteCast={onFaceTagTotallyVoteCast}
              onFaceTagNopeVoteCast={onFaceTagNopeVoteCast}
            />
            <PhotoCropModal
              forceOpen={cropMode}
              photoId={photo.id}
              identityId={identity?.id}
              albumFolderId={album?.root_album_folder?.id}
              matchedPhotoId={matchedPhotoId}
              defaultCrop={defaultCrop}
              onClose={() => setCropMode(false)}
            />
            <FeatureBlock condition={canAccessSpotlightOnBasename(basename)}>
              <PhotoGridViewerActionButton
                label="Spotlight"
                icon={<SpotlightIcon />}
                onClick={() => setHighlightsMode(true)}
              />
              <PhotoForFacesHighlightModal
                forceOpen={highlightsMode}
                photoRecord={photo}
                albumId={album.id}
                onClose={() => setHighlightsMode(false)}
              />
            </FeatureBlock>
            <PhotoGridViewerActionButton
              label="Custom Tags"
              icon={<TagIcon />}
              onClick={() =>
                openModal(PHOTO_ALBUM_FOLDER_TAGS, {
                  photoId: photo.id,
                  albumId: album.id,
                })
              }
            />
          </Fragment>
        )}
      </NoContributorPermissionBlock>
      {canAccessOriginalDownloadsOnBasename(basename) || isContributor ? (
        <div className="dropdown-menu-dark">
          <DropdownMenu
            toggle={
              <PhotoGridViewerActionButton
                label="Download Photo"
                icon={<DownloadIcon />}
                disabled={loadingDownload}
                className={classNames({
                  'is-loading': loadingDownload,
                })}
              />
            }
          >
            <Button
              btnStyle="reset"
              className="dropdown-item h-auto-i"
              disabled={loadingDownload}
              onClick={() => handleDownload()}
            >
              <p className="mb-0 text-left">
                Download hi-res
                <br />
                <span className="text-light text-xs">
                  Great for prints up to 8x10
                </span>
              </p>
            </Button>
            <Button
              btnStyle="reset"
              className="dropdown-item h-auto-i"
              disabled={loadingDownload}
              onClick={() => handleDownload({ downloadOriginal: true })}
            >
              <p className="mb-0 text-left">
                Download original
                <br />
                <span className="text-light text-xs">
                  Great for large format printing
                </span>
              </p>
            </Button>
            {photo?.foreground?.original_url && (
              <Button
                btnStyle="reset"
                className="dropdown-item h-auto-i"
                disabled={loadingDownload}
                onClick={() =>
                  handleDownload({ downloadPhotoForeground: true })
                }
              >
                <p className="mb-0 text-left">
                  Download knock-out
                  <br />
                  <span className="text-light text-xs">
                    Great for custom backdrops
                  </span>
                </p>
              </Button>
            )}
          </DropdownMenu>
        </div>
      ) : (
        <PhotoGridViewerActionButton
          label="Download Photo"
          icon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={loadingDownload}
          className={classNames({
            'is-loading': loadingDownload,
          })}
        />
      )}

      {isPublished &&
        !isReferencePhotosAlbum &&
        !album?.picture_day_enabled &&
        shareActionEnabled && (
          <Fragment>
            {photo.share_blocked ? (
              <PhotoGridViewerActionButton
                label="Share on Facebook"
                icon={<FacebookSquareIcon />}
                onClick={() => openModal(SHARE_BLOCK_MODAL)}
              />
            ) : (
              <PhotoGridViewerActionButton>
                {({ isMobile: isMobileResolution, className }) => (
                  <FacebookShareButton
                    className={className}
                    url={`${getConfig().app.baseUrl}/p/${photo?.id}`}
                    hashtag={
                      album?.hash_code && `#${removeHashes(album?.hash_code)}`
                    }
                    data-tip="Share on Facebook"
                    data-for="facebook-share-button"
                  >
                    <FacebookSquareIcon />
                    {isMobileResolution && (
                      <span className="ml-1">Share on Facebook</span>
                    )}
                    {!isMobileResolution && (
                      <ReactTooltip
                        id="facebook-share-button"
                        className="tooltip"
                      />
                    )}
                  </FacebookShareButton>
                )}
              </PhotoGridViewerActionButton>
            )}
          </Fragment>
        )}
      <NoContributorPermissionBlock entityId={album.id}>
        {isPublished && !isReferencePhotosAlbum && coverPhotoActionEnabled && (
          <CoverPhotoButton photoId={photo.id} album={album} skipQueries>
            {({ updateCoverPhoto, loading }) => {
              return (
                <PhotoGridViewerActionButton
                  label="Use as cover photo"
                  icon={<PhotosIcon />}
                  onClick={updateCoverPhoto}
                  disabled={loading}
                />
              );
            }}
          </CoverPhotoButton>
        )}

        {isPublished && hasSlideshow && (
          <NoContributorPermissionBlock entityId={album.id}>
            <PhotoGridViewerActionButton
              label={
                photo.is_album_slideshow_photo
                  ? 'Remove from Slideshow'
                  : 'Add to Slideshow'
              }
              icon={
                photo.is_album_slideshow_photo ? (
                  <div className="checked-slideshow">
                    <SlideshowIcon />
                    <div>
                      <CheckmarkIcon size={7} className="slideshow-checkmark" />
                    </div>
                  </div>
                ) : (
                  <SlideshowIcon />
                )
              }
              onClick={() => onSlideshowToggle(photo)}
            />
          </NoContributorPermissionBlock>
        )}

        {onFavoritePhotoToggle && photo && !album?.picture_day_enabled && (
          <PhotoGridViewerActionButton
            label={isFavorite ? 'Remove Favorite' : 'Favorite Photo'}
            icon={
              isFavorite ? (
                <FavoritesIcon color="#fe5851" />
              ) : (
                <FavoritesEmptyIcon />
              )
            }
            onClick={onFavoritePhotoToggle}
          />
        )}

        {/* Only show this if the photo is not published. Otherwise we will get the appropriate slideshow toggle. */}
        {onSlideshowVisibilityToggle && photo && !isPublished && (
          <PhotoGridViewerActionButton
            label="Publish & Add to Slideshow"
            icon={<SlideshowIcon />}
            onClick={() => {
              if (isMobile()) {
                requestConfirm({
                  message: (
                    <>
                      <h3 className="text-center">
                        Are you sure you want to publish this photo and add it
                        to the slideshow?
                      </h3>
                    </>
                  ),
                  confirmText: 'Publish',
                  cancelText: 'Go back',
                  onConfirm: onSlideshowVisibilityToggle,
                  onCancel: () => {},
                });
              } else {
                onSlideshowVisibilityToggle();
              }
            }}
          />
        )}

        {onVisibilityToggle && photo && (
          <PhotoGridViewerActionButton
            label={
              isPublished
                ? 'Hide'
                : 'Photo is hidden from others. Click to unhide.'
            }
            className={!isPublished ? 'text-danger' : ''}
            icon={isPublished ? <EyeIcon /> : <EyeSlashIcon color="#fe5851" />}
            onClick={() => {
              if (isMobile()) {
                requestConfirm({
                  message: (
                    <>
                      <h3 className="text-center">
                        Are you sure you want to{' '}
                        {isPublished ? 'hide' : 'publish'} this photo?
                      </h3>
                    </>
                  ),
                  confirmText: isPublished ? 'Hide' : 'Publish',
                  cancelText: 'Go back',
                  onConfirm: onVisibilityToggle,
                  onCancel: () => {},
                });
              } else {
                onVisibilityToggle();
              }
            }}
          />
        )}

        {onDelete && (
          <PhotoGridViewerActionButton
            label="Delete"
            icon={<TrashIcon />}
            onClick={onDelete}
          />
        )}
      </NoContributorPermissionBlock>
    </Fragment>
  );
};

PhotoGroupPhotoViewerActions.propTypes = propTypes;

export default PhotoGroupPhotoViewerActions;
